import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Navigate, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Box, CircularProgress } from '@mui/material';
import { AuthProvider } from './context/AuthContext.jsx';
import ProtectedRoute from './layouts/ProtectedRoute';
import { DEFAULT_ADMIN_PATH } from './utils/adminAccess.js';

const LoginPage = lazy(() => import('./pages/LoginPage'));
const OverviewPage = lazy(() => import('./pages/OverviewPage.jsx'));
const PosBranchesPage = lazy(() => import('./pages/retail/PosBranchesPage.jsx'));
const PosTenantOnboardingPage = lazy(() => import('./pages/retail/PosTenantOnboardingPage.jsx'));
const SuppliersPage = lazy(() => import('./pages/SuppliersPage.jsx'));
const PosEquityPartnersPage = lazy(() => import('./pages/retail/PosEquityPartnersPage.jsx'));
const AccountsPage = lazy(() => import('./pages/AccountsPage.jsx'));
const GlobalAuditPage = lazy(() => import('./pages/GlobalAuditPage.jsx'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { refetchOnWindowFocus: false, retry: 1 },
  },
});

function RouteLoader() {
  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
      <CircularProgress />
    </Box>
  );
}

function renderLazy(PageComponent) {
  return (
    <Suspense fallback={<RouteLoader />}>
      <PageComponent />
    </Suspense>
  );
}

/**
 * Paths from the old marketplace admin that still have a home here, so
 * bookmarks keep working. Everything else falls through to the overview.
 */
const LEGACY_REDIRECTS = {
  dashboard: '/overview',
  users: '/accounts',
  vendors: '/accounts',
  'admin/pos/branches': '/branches',
  'admin/retail/equity-partners': '/equity-partners',
  'admin/retail/tenant-onboarding': '/workspaces',
  'admin/audit': '/audit',
};

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={renderLazy(LoginPage)} />
      <Route path="/login" element={renderLazy(LoginPage)} />
      <Route path="/*" element={<ProtectedRoute />}>
        <Route path="overview" element={renderLazy(OverviewPage)} />
        <Route path="branches" element={renderLazy(PosBranchesPage)} />
        <Route path="workspaces" element={renderLazy(PosTenantOnboardingPage)} />
        <Route path="suppliers" element={renderLazy(SuppliersPage)} />
        <Route path="equity-partners" element={renderLazy(PosEquityPartnersPage)} />
        <Route path="accounts" element={renderLazy(AccountsPage)} />
        <Route path="audit" element={renderLazy(GlobalAuditPage)} />
        {Object.entries(LEGACY_REDIRECTS).map(([from, to]) => (
          <Route key={from} path={from} element={<Navigate to={to} replace />} />
        ))}
        <Route path="*" element={<Navigate to={DEFAULT_ADMIN_PATH} replace />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <AppRoutes />
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
